import { useState, useEffect, useRef } from "react";
import { Input, Typography, Flex, Alert } from "antd";
import CustomButton from "../../components/CustomButton/Button";
import ArrowLeftIcon from "../../assets/icons/arrow-left.svg";
import { useDispatch, useSelector } from "react-redux";
import {
    validateOtp,
    getBusinessDetailedView,
    editValidateOtp,
    sendOtp,
    editSendOtp,
} from "../../redux/slices/BusinessProfileSlice";
import "./OtpContent.scss";
import { BTN_TXT, DESC_TXT } from "../../constants/constants";
import { logout } from "../../redux/slices/AuthSlice";

const { Title, Text } = Typography;

const numberOfDigits = 6;

const OtpContent = ({
    onClose,
    oldDetailOtpModalOpen,
    setNewDetailModalOpen,
    newDetailOtpModalOpen,
    newDetail,
    setNewDetail,
    isMobileOtp,
    oldDetail
    
}) => {
    const [otp, setOtp] = useState(new Array(numberOfDigits).fill(""));
    const [seconds, setSeconds] = useState(60);
    const [isResendEnabled, setIsResendEnabled] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [resendError, setResendError] = useState(null);
    const otpBoxReference = useRef([]);
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const {
        otpData,
        error,
        editValidateOtpError,
        validateOtpLoading,
    } = useSelector((state) => state.profile1);

    // New-email/phone OTP must never show Send OTP errors (e.g. "already verified").
    const otpAlertMessage = (newDetailOtpModalOpen || newDetail)
        ? (editValidateOtpError || resendError)
        : error;

    const isOtpFilled = otp.every((digit) => digit !== "");

    useEffect(() => {
        if (oldDetailOtpModalOpen || newDetailOtpModalOpen) {
            setOtp(new Array(numberOfDigits).fill(""));
            setSeconds(60);
            setIsResendEnabled(false);
        }
    }, [oldDetailOtpModalOpen, newDetailOtpModalOpen]);

    useEffect(() => {
        if (newDetailOtpModalOpen) {
            setResendError(null);
        }
    }, [newDetailOtpModalOpen]);

    useEffect(() => {
        if (seconds > 0) {
            const timerId = setTimeout(() => setSeconds(seconds - 1), 1000);
            return () => clearTimeout(timerId);
        } else {
            setIsResendEnabled(true);
        }
    }, [seconds]);

    const backToChangeDetail = () => {
        onClose();
        setNewDetailModalOpen(true);
    };

    const buildContactPayload = (contact) => {
        if (isMobileOtp) {
            return { mobile: contact };
        }
        return { email: contact };
    };

    const handleResendOtp = async () => {
        if (resendLoading) return;

        const contact = newDetail || oldDetail;
        if (!contact) return;

        setResendLoading(true);
        try {
            const payload = buildContactPayload(contact);
            const action = newDetail ? editSendOtp(payload) : sendOtp(payload);
            const res = await dispatch(action);

            if (res?.meta?.requestStatus === "rejected") {
                const message = res.payload?.message || res.payload;
                // Send-step "already verified" belongs on Enter New Email, not Verify OTP.
                if (typeof message === "string" && /already verified/i.test(message)) {
                    onClose();
                    setNewDetailModalOpen?.(true);
                    return;
                }
                setResendError(message);
                return;
            }

            if (res?.payload?.statusCode === 201) {
                setResendError(null);
                setOtp(new Array(numberOfDigits).fill(""));
                setSeconds(60);
                setIsResendEnabled(false);
                otpBoxReference.current[0]?.focus?.();
            }
        } finally {
            setResendLoading(false);
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text").trim().slice(0, numberOfDigits);
        if (!pastedData) return;

        const digits = pastedData.split("");
        const newArr = [...otp];

        digits.forEach((digit, i) => {
            if (i < numberOfDigits) {
                newArr[i] = digit;
            }
        });

        setOtp(newArr);

        const nextFocusIndex = Math.min(digits.length, numberOfDigits - 1);
        otpBoxReference.current[nextFocusIndex]?.focus();
    };

    const handleChange = (value, index) => {
        if (value.length > 1) {
            const digits = value.trim().slice(0, numberOfDigits).split("");
            const newArr = [...otp];
            digits.forEach((digit, i) => {
                if (i < numberOfDigits) {
                    newArr[i] = digit;
                }
            });
            setOtp(newArr);
            const nextFocusIndex = Math.min(digits.length, numberOfDigits - 1);
            otpBoxReference.current[nextFocusIndex]?.focus();
            return;
        }

        let newArr = [...otp];
        newArr[index] = value;
        setOtp(newArr);

        if (value && index < numberOfDigits - 1) {
            otpBoxReference.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            otpBoxReference.current[index - 1]?.focus();
        }
        if (e.key === "Enter") {
            if (isOtpFilled) {
                handleVerifyOtp();
            } else if (otp[index] && index < numberOfDigits - 1) {
                otpBoxReference.current[index + 1]?.focus();
            }
        }
    };

    const handleVerifyOtp = async () => {
        const otpString = otp.join("");

        const payload = {
            email: newDetail ? newDetail : oldDetail,
            otp: otpString,
            reqId: otpData?.response?.reqId,
            docId: otpData?.response?.docId,
        }

        if (isMobileOtp) {
            delete payload.email
            payload.mobile = newDetail ? newDetail : oldDetail
        }

        const response = await dispatch(newDetail ? editValidateOtp(payload) : validateOtp(payload))

        if (response?.payload?.statusCode === 201) {
            onClose();
            setOtp(new Array(numberOfDigits).fill(""));
            if (newDetail) {
                const updatedContact = newDetail;
                setNewDetail(null);
                dispatch(getBusinessDetailedView(user?.orgId));
                if (!isMobileOtp) {
                    try {
                        sessionStorage.setItem("emailUpdatedNote", "1");
                        if (updatedContact) {
                            sessionStorage.setItem("loginEmailPrefill", updatedContact);
                        }
                        await dispatch(logout(user?.userId));
                    } finally {
                        localStorage.clear();
                        window.location.href = "/login?emailUpdated=1";
                    }
                }
            } else {
                setNewDetailModalOpen(true);
            }
        }

    };

    return (
        <div className="otp-content-container">
            {newDetailOtpModalOpen && (
                <img
                    src={ArrowLeftIcon}
                    alt="Arrow-left"
                    className="back-arrow"
                    onClick={backToChangeDetail}
                />
            )}
            <Title level={3}>Verify OTP</Title>
            <Text style={{ fontSize: 14, textAlign: "center" }}>
                {newDetail
                    ? `We have sent a 6-digit OTP to ${newDetail}. Please check your ${isMobileOtp ? "phone" : "email"}.`
                    : `Please enter the OTP sent to your registered ${isMobileOtp ? "phone number" : "email"} to confirm it's really you.`
                }
            </Text>
            <Flex
                align="center"
                justify="space-between"
                style={{ marginTop: 70, width: "100%" }}
            >
                {otp.map((digit, index) => (
                    <Input
                        key={index}
                        value={digit}
                        onChange={(e) => handleChange(e.target.value, index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        onPaste={handlePaste}
                        ref={(reference) => (otpBoxReference.current[index] = reference)}
                        style={{ textAlign: "center", width: 50.6 }}
                    />
                ))}
            </Flex>
            {otpAlertMessage && (
                <Alert
                    style={{ marginTop: 20 }}
                    message={otpAlertMessage?.message || otpAlertMessage}
                    type="error"
                    showIcon
                />
            )}
            <CustomButton
                type="primary"
                block
                onClick={handleVerifyOtp}
                disabled={!isOtpFilled}
                loading={validateOtpLoading}
                className="otp-submit-btn"
            >
                {BTN_TXT.VERIFY_OTP}
            </CustomButton>

            {isResendEnabled ? (
                <Text
                    onClick={resendLoading ? undefined : handleResendOtp}
                    style={{ cursor: resendLoading ? "not-allowed" : "pointer", opacity: resendLoading ? 0.6 : 1 }}
                >
                    {resendLoading ? "Sending..." : BTN_TXT.RESEND_OTP}
                </Text>
            ) : (
                <Text>
                    Resends in {seconds} {seconds === 1 ? "second" : "seconds"}
                </Text>
            )}
            {!isMobileOtp && (
                <Text style={{ color: "#40566D", marginTop: 20, fontSize: 12, textAlign: "center" }}>
                    {DESC_TXT.OTP_INFO_TEXT}
                </Text>
            )}
        </div>
    );
};

export default OtpContent;
